import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { DiscoveryHero } from "../components/discovery/DiscoveryHero";
import { DiscoveryBio } from "../components/discovery/DiscoveryBio";
import { DiscoveryForm } from "../components/discovery/DiscoveryForm";
import { DiscoveryPaymentResult } from "../components/discovery/DiscoveryPaymentResult";

export default function Discovery() {
  const [params, setParams] = useSearchParams();
  const reference = params.get("reference");
  const [config, setConfig] = useState(null);
  const [pay, setPay] = useState({ state: reference ? "checking" : null, booking: null });

  const loadConfig = useCallback(() => api.get("/public/discovery/config").then((r) => setConfig(r.data)).catch(() => {}), []);
  useEffect(() => { loadConfig(); }, [loadConfig]);

  useEffect(() => {
    if (!reference) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.post(`/public/discovery/poll/${reference}`);
        if (data.payment_status === "paid") { setPay({ state: "paid", booking: data.booking }); loadConfig(); return; }
        if (data.payment_status === "failed" || tries >= 5) { setPay({ state: "failed", booking: data.booking }); return; }
        tries += 1; setTimeout(poll, 2500);
      } catch { setPay({ state: "failed", booking: null }); }
    };
    poll();
  }, [reference, loadConfig]);

  const retry = () => { setParams({}); setPay({ state: null, booking: null }); loadConfig(); };

  return (
    <div className="min-h-screen bg-white text-neutral-900" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="discovery-page">
      <DiscoveryHero />
      <DiscoveryBio />
      <section id="discovery-form" className="mx-auto max-w-6xl px-5 py-16 sm:py-20" data-testid="discovery-booking">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.3fr] lg:gap-16">
          <div>
            <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#0A8B85]">Reserve your slot</p>
            <h2 className="mt-3 text-3xl font-extrabold text-[#0B2A28] sm:text-4xl">Book your session</h2>
            <p className="mt-4 text-base leading-relaxed text-neutral-600 md:text-lg">
              Each slot holds one business at a time, so you get Doreen's full attention. Complete the form, pay securely,
              and your confirmation and calendar invite arrive straight away.
            </p>
          </div>
          <div className="rounded-3xl border border-neutral-200 bg-white p-6 shadow-[0_24px_60px_-30px_rgba(11,42,40,0.35)] sm:p-8" data-testid="discovery-form-card">
            {pay.state ? <DiscoveryPaymentResult state={pay.state} booking={pay.booking} onRetry={retry} />
              : !config ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#0A8B85]" /></div>
              : <DiscoveryForm config={config} onRefresh={loadConfig} />}
          </div>
        </div>
      </section>
      <footer className="border-t border-neutral-200 px-5 py-8 text-center text-xs text-neutral-500" data-testid="discovery-footer">
        © {new Date().getFullYear()} Clinq Africa · <Link to="/privacy-policy" className="hover:text-[#0A8B85]">Privacy policy</Link> · <Link to="/terms" className="hover:text-[#0A8B85]">Terms</Link>
      </footer>
    </div>
  );
}
